import { instanceKey, orderedEntries, REPEATABLE } from "./order";
import { chosen, type Field, type Form, type Model } from "./forms";
import type { Words } from "./formI18n";
import { settingId } from "./useForm";

/**
 * ═══ LES CHAMPS DU FORMULAIRE SUR LE RÉCAPITULATIF ═══
 *
 * Le récapitulatif de la confirmation ne disait que ce que le tunnel collecte en propre :
 * qui, comment, sous quel profil. Ce que le formulaire d'informations demande — un
 * téléphone, un régime alimentaire, une taille de t-shirt, n'importe quelle donnée
 * étendue — n'y figurait jamais, alors que c'est précisément ce qu'on revient vérifier.
 *
 * L'organisateur coche ici les champs à montrer (`FO_MEETING_V2_RECAP_FIELDS`), parmi
 * ceux des formulaires d'informations servis dans le parcours. Rien de coché, rien
 * d'ajouté : la carte des événements existants ne bouge pas.
 *
 * ⚠️ UN CHAMP EST DÉSIGNÉ PAR SON MODÈLE ET SA CLÉ, pas par son identifiant de ligne.
 * `U:phone` reste `U:phone` quand le formulaire est réécrit par l'éditeur du panneau —
 * `PUT /forms/{id}` supprime et réinsère toutes ses lignes, donc leurs identifiants
 * changent à chaque sauvegarde — et quand le même champ figure dans deux instances de
 * l'étape. La VALEUR, elle, vit sous le modèle et la clé : c'est exactement ce qu'il
 * faut pour la relire (voir `valueOf`).
 */

export const RECAP_FIELDS_KEY = "FO_MEETING_V2_RECAP_FIELDS";

/** La clé du formulaire de la première étape informations ; les autres sont suffixées. */
export const INFO_FORM_KEY = "FO_MEETING_V2_INFO_FORM_ID";

export type FieldRef = { model: Model; key: string };

const MODELS: ReadonlySet<string> = new Set(["U", "UE", "C"]);

/**
 * Ce que le récapitulatif dit déjà par ses propres lignes : ces champs ont leur
 * interrupteur, les proposer ici les ferait figurer deux fois.
 */
export const ALREADY_SHOWN: ReadonlySet<string> = new Set([
  "U:firstName",
  "U:lastName",
  "U:email",
]);

/** Ce qui n'a pas de valeur à montrer : un décor, un fichier. */
const SILENT = new Set<Field["component"]>(["comment", "html", "file"]);

export function refOf(field: Pick<Field, "model" | "key">): string {
  return `${field.model}:${field.key}`;
}

/** `U:phone,UE:regime` → références ; ce qui n'a pas cette forme est ignoré. */
export function readFieldRefs(raw: unknown): FieldRef[] {
  const parts = Array.isArray(raw)
    ? raw.map(String)
    : typeof raw === "string"
      ? raw.split(",")
      : [];
  const seen = new Set<string>();
  const out: FieldRef[] = [];
  for (const part of parts) {
    const [model, ...rest] = part.trim().split(":");
    const key = rest.join(":").trim();
    if (!model || !MODELS.has(model) || !key) continue;
    const ref = `${model}:${key}`;
    if (seen.has(ref)) continue;
    seen.add(ref);
    out.push({ model: model as Model, key });
  }
  return out;
}

export function writeFieldRefs(refs: ReadonlyArray<FieldRef>): string {
  return refs.map(refOf).join(",");
}

/**
 * LES CLÉS DE RÉGLAGE des formulaires d'informations, une par instance de l'étape et
 * dans l'ordre du parcours — la première sans suffixe, comme partout (`instanceKey`).
 */
export function infoFormKeys(orderRaw: unknown): string[] {
  return orderedEntries(orderRaw)
    .filter((e) => e.key === REPEATABLE)
    .map((e) => instanceKey(INFO_FORM_KEY, e.instance));
}

/** Les formulaires que ces réglages désignent, sans doublon, dans l'ordre du parcours. */
export function servedForms(
  keys: ReadonlyArray<string>,
  value: (key: string) => unknown,
  forms: ReadonlyArray<Form>,
): Form[] {
  const out: Form[] = [];
  for (const key of keys) {
    const id = settingId(value(key));
    if (id === undefined) continue;
    const form = forms.find((f) => String(f.id) === id);
    if (form && !out.includes(form)) out.push(form);
  }
  return out;
}

/** Un champ et le formulaire où il est posé (pour ses libellés traduits). */
export type Candidate = { field: Field; form: Form };

/**
 * LES CHAMPS QU'ON PEUT COCHER : ceux qui portent une valeur, une fois chacun. Le même
 * champ posé dans deux instances n'apparaît qu'une fois — c'est la même réponse.
 */
export function candidates(forms: ReadonlyArray<Form>): Candidate[] {
  const seen = new Set<string>();
  const out: Candidate[] = [];
  for (const form of forms) {
    for (const field of form.fields) {
      if (!field.key || SILENT.has(field.component)) continue;
      const ref = refOf(field);
      if (ALREADY_SHOWN.has(ref) || seen.has(ref)) continue;
      seen.add(ref);
      out.push({ field, form });
    }
  }
  return out;
}

/** Les candidats retenus, dans l'ordre où l'organisateur les a cochés. */
export function selectedCandidates(
  refs: ReadonlyArray<FieldRef>,
  all: ReadonlyArray<Candidate>,
): Candidate[] {
  return refs.flatMap((r) => {
    const found = all.find((c) => c.field.model === r.model && c.field.key === r.key);
    return found ? [found] : [];
  });
}

/**
 * LA VALEUR D'UN CHAMP, EN TOUTES LETTRES.
 *
 * Une réponse à choix s'écrit avec les libellés de ses options, dans la langue lue, et
 * non avec leurs clés ; une case cochée sans option dit « oui » ; le reste s'écrit tel
 * quel. Vide, la ligne ne s'affiche pas (règle de `Row`).
 */
export function formatFieldValue(
  field: Field,
  raw: unknown,
  words: Words | undefined,
  yes: string,
): string {
  if (raw === null || raw === undefined || raw === "") return "";
  if (field.options.length > 0) {
    const keys = chosen(raw);
    return keys
      .map((k) => {
        const option = field.options.find((o) => o.value === k);
        return option ? (words ? words.option(field, option) : option.label) : k;
      })
      .filter((s) => s.trim() !== "")
      .join(" · ");
  }
  if (raw === true) return yes;
  if (raw === false) return "";
  if (Array.isArray(raw)) return raw.map(String).filter(Boolean).join(" · ");
  if (typeof raw === "object") return chosen(raw).join(" · ");
  return String(raw).trim();
}
